import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    IMG_CDN_URL,
    ITEM_IMG_CDN_URL,
    MENU_ITEM_TYPE_KEY,
    RESTAURANT_TYPE_KEY,
} from "../constants";
import { addItem } from "../utils/cartSlice";
import { MenuItem, RestaurantInfo } from "../types";

const ITEMS_PER_PAGE = 6;

const RestaurantMenu: React.FC = () => {
    const { resId } = useParams<{ resId: string }>();
    const navigate = useNavigate();
    const [restaurant, setRestaurant] = useState<RestaurantInfo | null>(null);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState<boolean>(true);
    const dispatch = useDispatch();

    useEffect(() => {
        fetchRestaurantInfo();
    }, [resId]);

    const addToCart = (item: MenuItem) => {
        dispatch(addItem(item));
        setAddedItems(prev => new Set(prev).add(item.id));

        setTimeout(() => {
            setAddedItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(item.id);
                return newSet;
            });
        }, 2000);
    };

    const fetchRestaurantInfo = async () => {
        if (!resId) return;

        setLoading(true);
        try {
            // Using CORS Proxy to bypass CORS issues with Swiggy API
            const swiggyMenuUrl = `https://www.swiggy.com/dapi/menu/pl?page-type=REGULAR_MENU&complete-menu=true&lat=21.1702401&lng=72.83106070000001&restaurantId=${resId}&submitAction=ENTER`;
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(swiggyMenuUrl)}`;

            const response = await fetch(proxyUrl);
            const json = await response.json();

            const restaurantData =
                json?.data?.cards
                    ?.map((x: any) => x.card)
                    ?.find((x: any) => x && x.card["@type"] === RESTAURANT_TYPE_KEY)?.card
                    ?.info || null;

            setRestaurant(restaurantData);

            const menuItemsData =
                json?.data?.cards
                    .find((x: any) => x.groupedCard)
                    ?.groupedCard?.cardGroupMap?.REGULAR?.cards?.map(
                        (x: any) => x.card?.card
                    )
                    ?.filter((x: any) => x["@type"] === MENU_ITEM_TYPE_KEY)
                    ?.map((x: any) => x.itemCards)
                    .flat()
                    .map((x: any) => x.card?.info) || [];

            const uniqueMenuItems: MenuItem[] = [];
            menuItemsData.forEach((item: MenuItem) => {
                if (item && !uniqueMenuItems.find((x) => x.id === item.id)) {
                    uniqueMenuItems.push(item);
                }
            });

            setMenuItems(uniqueMenuItems);
        } catch (error) {
            setMenuItems([]);
            setRestaurant(null);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number | undefined, defaultPrice: number | undefined): string => {
        const finalPrice = price || defaultPrice || 0;
        if (finalPrice > 0) {
            return new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
            }).format(finalPrice / 100);
        }
        return "";
    };

    const getRatingColor = (rating: number | undefined): string => {
        if (!rating || rating === 0) return "bg-gray-400";
        if (rating >= 4) return "bg-green-500";
        if (rating >= 3) return "bg-yellow-500";
        return "bg-red-500";
    };

    // Pagination logic
    const totalItems = menuItems.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentMenuItems = menuItems.slice(startIndex, endIndex);

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            const menuSection = document.getElementById('menu-section');
            if (menuSection) {
                menuSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const getPageNumbers = (): (number | string)[] => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    if (loading) {
        return (
            <div className="pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-200">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400">Loading restaurant...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            {/* Back Button */}
            <div className="bg-white dark:bg-gray-900 border-b dark:border-gray-800 sticky top-16 z-40 transition-colors">
                <div className="max-w-4xl mx-auto px-6 py-3">
                    <button
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                        <span>Back to restaurants</span>
                    </button>
                </div>
            </div>

            {/* Restaurant Summary */}
            <div className="bg-white dark:bg-gradient-to-r dark:from-gray-900 dark:to-gray-800 text-gray-900 dark:text-white py-12 px-6 transition-colors shadow-sm dark:shadow-none">
                <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
                    <img
                        src={IMG_CDN_URL + restaurant?.cloudinaryImageId}
                        alt={restaurant?.name}
                        className="w-64 h-48 object-cover rounded-2xl shadow-2xl"
                    />
                    <div className="text-center md:text-left flex-1">
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">
                            {restaurant?.name}
                        </h1>
                        <p className="text-gray-500 dark:text-gray-300 mb-2">
                            {Array.isArray(restaurant?.cuisines)
                                ? restaurant?.cuisines?.join(", ")
                                : restaurant?.cuisines}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                            {restaurant?.areaName || restaurant?.locality}
                        </p>
                        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm">
                            <span
                                className={`${getRatingColor(restaurant?.avgRating)} px-3 py-1 rounded-md font-semibold flex items-center gap-1`}
                            >
                                <i className="fa-solid fa-star text-xs"></i>
                                {restaurant?.avgRating}
                                {restaurant?.totalRatingsString && (
                                    <span className="text-white/90 ml-1">({restaurant.totalRatingsString})</span>
                                )}
                            </span>
                            <span className="flex items-center gap-1 font-medium text-gray-700 dark:text-white">
                                <i className="fa-solid fa-clock text-orange-500 dark:text-orange-400"></i>
                                {restaurant?.sla?.slaString}
                            </span>
                            <span className="flex items-center gap-1 font-medium text-gray-700 dark:text-white">
                                <i className="fa-solid fa-indian-rupee-sign text-orange-500 dark:text-orange-400"></i>
                                {restaurant?.costForTwoMessage || restaurant?.costForTwo}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Menu Items */}
            <div id="menu-section" className="max-w-4xl mx-auto px-6 py-12">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Recommended</h2>
                        <p className="text-gray-500 dark:text-gray-400">{totalItems} items</p>
                    </div>
                    {totalPages > 1 && (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                            Page {currentPage} of {totalPages}
                        </p>
                    )}
                </div>

                {menuItems.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">🍽️</div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            No menu items available
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400">
                            This restaurant's menu is currently unavailable
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {currentMenuItems.map((item) => (
                            <div
                                key={item.id}
                                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between gap-6"
                            >
                                <div className="flex-1">
                                    {/* Veg/Non-veg indicator */}
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`w-4 h-4 border-2 rounded-sm flex items-center justify-center ${item.isVeg === 1 ? 'border-green-500' : 'border-red-500'
                                            }`}>
                                            <span className={`w-2 h-2 rounded-full ${item.isVeg === 1 ? 'bg-green-500' : 'bg-red-500'
                                                }`}></span>
                                        </span>
                                        {item.ratings?.aggregatedRating?.rating && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                <i className="fa-solid fa-star text-orange-400"></i>
                                                {item.ratings.aggregatedRating.rating}
                                                {item.ratings.aggregatedRating.ratingCount && (
                                                    <span>({item.ratings.aggregatedRating.ratingCount})</span>
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                        {item.name}
                                    </h3>
                                    <p className="font-semibold text-orange-500 mb-2">
                                        {formatPrice(item.price, item.defaultPrice)}
                                    </p>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-2">
                                        {item.description}
                                    </p>
                                </div>
                                <div className="flex flex-col items-center gap-3">
                                    {item.imageId && (
                                        <img
                                            src={ITEM_IMG_CDN_URL + item.imageId}
                                            alt={item.name}
                                            className="w-28 h-24 object-cover rounded-xl"
                                        />
                                    )}
                                    <button
                                        onClick={() => addToCart(item)}
                                        className={`font-semibold px-8 py-2 rounded-lg transition-all duration-300 ${addedItems.has(item.id)
                                            ? "bg-green-500 text-white"
                                            : "bg-orange-500 hover:bg-orange-600 text-white"
                                            }`}
                                    >
                                        {addedItems.has(item.id) ? (
                                            <>
                                                <i className="fa-solid fa-check mr-2"></i>
                                                Added
                                            </>
                                        ) : (
                                            "ADD +"
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-12">
                        <button
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                                ? "text-gray-400 cursor-not-allowed dark:text-gray-600"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
                        >
                            <i className="fa-solid fa-chevron-left text-sm"></i>
                            <span className="hidden sm:inline">Previous</span>
                        </button>

                        <div className="flex items-center gap-1">
                            {getPageNumbers().map((page, index) => (
                                <button
                                    key={index}
                                    onClick={() => typeof page === 'number' && goToPage(page)}
                                    disabled={page === '...'}
                                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${page === currentPage
                                        ? "bg-orange-500 text-white"
                                        : page === '...'
                                            ? "text-gray-400 cursor-default"
                                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                                ? "text-gray-400 cursor-not-allowed dark:text-gray-600"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
                        >
                            <span className="hidden sm:inline">Next</span>
                            <i className="fa-solid fa-chevron-right text-sm"></i>
                        </button>
                    </div>
                )}

                {totalPages > 1 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-4">
                        Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items
                    </p>
                )}
            </div>
        </div>
    );
};

export default RestaurantMenu;
